import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import transformWizardStyles from '../../helpers/transformWizardStyles';
import useFetch from '../../helpers/useFetch';

// The wording the layout editor has always used for a body it cannot render.
// Shared with the preview frame, which now reports the compile failures that used
// to surface here: either way it is the merchant's own markup at fault.
export const TEMPLATE_MARKUP_ERROR = 'Unable to show preview due to template markup errors';

// Assembles what the SDK's `renderRecommender` takes: the layout body and real
// products from the domain — two discounted and two not, so both price branches
// render. Nothing is compiled here; the preview frame hands this to the same
// code that renders a block on the shop, which compiles the body it is given
// verbatim and reports a markup error back out.
const usePreviewRecommender = (template) => {
  const [fetchError, setFetchError] = useState();
  const [previewError, setPreviewError] = useState();
  const [nonDiscountedProducts, setNonDiscountedProducts] = useState();
  const [discountedProducts, setDiscountedProducts] = useState();
  const { domain } = useSelector((state) => state.domain);
  const [previewRecommender, setPreviewRecommender] = useState();
  const fetch = useFetch();

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/products?from=0&size=2&discounted=0`)
      .then(({ products }) => setNonDiscountedProducts(products))
      .catch(setFetchError);

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/products?from=0&size=2&discounted=1`)
      .then(({ products }) => setDiscountedProducts(products))
      .catch(setFetchError);
  }, [domain.accountId, domain.id, fetch]);

  useEffect(() => {
    if (!template || discountedProducts === undefined || nonDiscountedProducts === undefined) {
      return;
    }

    const products = [...discountedProducts, ...nonDiscountedProducts];

    const productsCustomFields = products.map((product) => product.custom);

    const domainSearchBarFilters = domain?.searchBarFilters || [];

    const transformedFilters = domainSearchBarFilters.map((filter) => {
      return {
        key: filter.key,
        title: filter.title,
        options: [],
      };
    });

    if (domainSearchBarFilters.length) {
      productsCustomFields.forEach((productFields, index) => {
        Object.keys(productFields).forEach((type) => {
          productFields[type].forEach((customField) => {
            const matchingFilter = transformedFilters.find(
              (filter) => filter.key.toLowerCase() === customField.key.toLowerCase()
            );

            if (!matchingFilter) {
              return;
            }

            const mappedValues = customField.values.map((v) => ({ value: v, label: v, selected: index === 0 }));

            mappedValues.forEach((mappedOption) => {
              const optionValueExists = matchingFilter.options.some((opt) => opt.value === mappedOption.value);

              if (optionValueExists) {
                return;
              }

              matchingFilter.options.push(mappedOption);
            });
          });
        });
      });
    }

    const params =
      template.scope === 'web'
        ? {
            token: 'someToken',
            title: 'Sample Product Block Name',
            response: products,
          }
        : { products, filters: transformedFilters };

    try {
      setPreviewRecommender({
        ...params,
        // NOTE: not sure why we need to transform styles a second time
        // (we already do this on every wizard change in TemplateForm.js)
        // but if removed styles from wizard don't appear the preview
        // perhaps it is some escaping or something
        template: { body: template.advancedEditor ? template.body : transformWizardStyles(template) },
      });
      setPreviewError(undefined);
    } catch (e) {
      // A body the wizard transform cannot read — hand-edited, or from an older
      // editor. Nothing to preview, so say so instead of tearing the page down.
      setPreviewRecommender(undefined);
      setPreviewError(`${TEMPLATE_MARKUP_ERROR}: ${e.message}.`);
    }
  }, [template, discountedProducts, nonDiscountedProducts, domain.searchBarFilters]);

  return {
    previewRecommender,
    previewError,
    fetchError,
    // The products drive the render, and assembling the recommender from them is
    // an effect of its own — one render after they arrive. Waiting on the
    // assembled object rather than on the requests keeps the frame from being
    // built once with nothing in it and then again with the block.
    loading: previewRecommender === undefined && previewError === undefined,
  };
};

export default usePreviewRecommender;
